import { useCallback, useState } from "react";

import { usePostHog } from "posthog-js/react";

import {
  COMMAND_LINK_ANALYTICS_SOURCE,
  NO_CAPACITY_REJECTED_MESSAGE,
  RECOVERABLE_OUTCOME_MESSAGES,
  SubmitQuoteCommandLinkAction,
} from "./constants";
import { dispatchSubmitQuoteExecuteResult } from "./utils";
import { ClientAppEvent, CommandLinkName } from "../../../../constants";
import { useCommandLinkExecute } from "../../hooks/useCommandLinkExecute";

import type { SubmitQuoteExecuteResponse } from "./types";

interface NoCapacityExecuteParams {
  action:
    | typeof SubmitQuoteCommandLinkAction.MARK_NO_CAPACITY
    | typeof SubmitQuoteCommandLinkAction.UNDO_NO_CAPACITY;
}

// Module-level so the execute callbacks keep a stable identity across renders.
const MARK_PARAMS: NoCapacityExecuteParams = {
  action: SubmitQuoteCommandLinkAction.MARK_NO_CAPACITY,
};
const UNDO_PARAMS: NoCapacityExecuteParams = {
  action: SubmitQuoteCommandLinkAction.UNDO_NO_CAPACITY,
};

interface UseSubmitQuoteNoCapacityCommandLinkProps {
  token: string;
  shipmentId: number;
  shipperCompanyId: number;
  initialHasMarkedNoCapacity: boolean;
  // Only the recoverable codes with a page-level handoff (RECOVERABLE_OUTCOME_MESSAGES).
  onRecoverableFailure: (outcomeCode: string) => void;
  onBlockedFailure: (blockedReasonCode: string) => void;
}

interface UseSubmitQuoteNoCapacityCommandLinkResult {
  hasMarkedNoCapacity: boolean;
  markNoCapacity: VoidFunction;
  undoNoCapacity: VoidFunction;
  loading: boolean;
  error: string | null;
}

/**
 * Marks and undoes the carrier's "no truck available" answer through the signed
 * token. A live quote (ACTIVE_QUOTE_EXISTS) sends the carrier to the app, and a
 * shipment that closed since the page loaded is named, both through the landing's
 * handlers (docs/engineering/design/carrier_submit_quote_command_link_no_capacity_design.md §8).
 * Any other recoverable rejection is reported on the control itself: the
 * landing's own recoverable copy describes the quote form.
 */
export function useSubmitQuoteNoCapacityCommandLink(
  props: UseSubmitQuoteNoCapacityCommandLinkProps,
): UseSubmitQuoteNoCapacityCommandLinkResult {
  const {
    token,
    shipmentId,
    shipperCompanyId,
    initialHasMarkedNoCapacity,
    onRecoverableFailure,
    onBlockedFailure,
  } = props;
  const posthog = usePostHog();
  const [hasMarkedNoCapacity, setHasMarkedNoCapacity] = useState(
    initialHasMarkedNoCapacity,
  );
  const [rejectionMessage, setRejectionMessage] = useState<string | null>(null);

  const {
    execute: executeMark,
    loading: markLoading,
    error: markError,
  } = useCommandLinkExecute<
    SubmitQuoteExecuteResponse,
    NoCapacityExecuteParams
  >({
    commandName: CommandLinkName.SUBMIT_QUOTE,
    token,
    params: MARK_PARAMS,
  });

  const {
    execute: executeUndo,
    loading: undoLoading,
    error: undoError,
  } = useCommandLinkExecute<
    SubmitQuoteExecuteResponse,
    NoCapacityExecuteParams
  >({
    commandName: CommandLinkName.SUBMIT_QUOTE,
    token,
    params: UNDO_PARAMS,
  });

  const captureNoCapacityEvent = useCallback(
    (event: ClientAppEvent) => {
      posthog?.capture(event, {
        source: COMMAND_LINK_ANALYTICS_SOURCE,
        shipment_id: shipmentId,
        shipper_company_id: shipperCompanyId,
      });
    },
    [posthog, shipmentId, shipperCompanyId],
  );

  const buildOnResponse = useCallback(
    (marked: boolean, event: ClientAppEvent) =>
      (response: SubmitQuoteExecuteResponse) => {
        dispatchSubmitQuoteExecuteResult({
          response,
          onSuccess: () => {
            setHasMarkedNoCapacity(marked);
            captureNoCapacityEvent(event);
          },
          onBlockedFailure,
          onRecoverableFailure: (outcomeCode) => {
            if (
              outcomeCode != null &&
              RECOVERABLE_OUTCOME_MESSAGES[outcomeCode] != null
            ) {
              onRecoverableFailure(outcomeCode);
              return;
            }
            setRejectionMessage(NO_CAPACITY_REJECTED_MESSAGE);
          },
        });
      },
    [captureNoCapacityEvent, onBlockedFailure, onRecoverableFailure],
  );

  const markNoCapacity = useCallback(() => {
    setRejectionMessage(null);
    executeMark(buildOnResponse(true, ClientAppEvent.NO_CAPACITY_CLICKED));
  }, [executeMark, buildOnResponse]);

  const undoNoCapacity = useCallback(() => {
    setRejectionMessage(null);
    executeUndo(buildOnResponse(false, ClientAppEvent.NO_CAPACITY_UNDONE));
  }, [executeUndo, buildOnResponse]);

  return {
    hasMarkedNoCapacity,
    markNoCapacity,
    undoNoCapacity,
    loading: markLoading || undoLoading,
    error: markError ?? undoError ?? rejectionMessage,
  };
}
